import { ArrowRight, Check, ChevronDown, Play } from "lucide-react";
import { useState } from "react";
import { HEALTHCARE_PLAYBOOK_CONTENTS } from "@/data/companyProgramProof";
import "./AI30ConversionProof.css";

export function AI30ClientPlaybook({ caseStudyHref }: { caseStudyHref: string }) {
  return (
    <details className="ai30-client-playbook">
      <summary>Inside a past client&apos;s company playbook <ChevronDown aria-hidden="true" size={19} /></summary>
      <div className="ai30-client-playbook-content">
        <p>This playbook was delivered in the separate healthcare engagement. Your cohort&apos;s deliverables are agreed in your proposal.</p>
        <ul>{HEALTHCARE_PLAYBOOK_CONTENTS.map(item => <li key={item}><Check aria-hidden="true" size={16} /><span>{item}</span></li>)}</ul>
        <a className="ai30-text-link" href={caseStudyHref}>See what we delivered <ArrowRight aria-hidden="true" size={17} /></a>
      </div>
    </details>
  );
}

export function AI30BusinessCase() {
  return (
    <div className="ai30-business-case" id="business-case" aria-labelledby="ai30-business-case-title">
      <h3 id="ai30-business-case-title">Measure the whole job.</h3>
      <p>Compare the same work before and after AI, then decide what to keep, change, or stop.</p>
      <dl>
        <div><dt>Quality</dt><dd>Does the result meet the same standard? Record what it missed or got wrong.</dd></div>
        <div><dt>Total effort</dt><dd>Count preparation, review, corrections, and unexpected problems alongside the AI run.</dd></div>
        <div><dt>Cost and adoption</dt><dd>Include program, tool, implementation, and support costs. Check whether people keep using the workflow.</dd></div>
      </dl>
      <p className="ai30-business-case-note">Time saved creates capacity. Cash savings and ROI also depend on actual costs and financial effects.</p>
    </div>
  );
}

export type AI30PreviewVideo = {
  id: string;
  title: string;
  youtubeId: string;
  description: string;
};

function TrainingVideoPreview({ video }: { video: AI30PreviewVideo }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <details className="ai30-training-recording" onToggle={event => setIsOpen(event.currentTarget.open)}>
      <summary><Play aria-hidden="true" size={18} /><span>{video.title}</span><ChevronDown aria-hidden="true" size={19} /></summary>
      <div className="ai30-training-recording-content">
        <p>{video.description}</p>
        {isOpen && <div className="ai30-training-recording-frame"><iframe src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}`} title={`${video.title} explainer`} loading="lazy" allow="accelerometer; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>}
      </div>
    </details>
  );
}

// Pass only recordings reviewed against the current offer and public wording.
export function AI30TeachingPreview({ videos }: { videos: readonly AI30PreviewVideo[] }) {
  if (videos.length === 0) return null;

  return (
    <section className="ai30-container ai30-teaching-preview" id="teaching-preview" aria-labelledby="ai30-teaching-preview-title">
      <div className="ai30-teaching-preview-heading"><p className="ai30-eyebrow">Preview the approach</p><h2 id="ai30-teaching-preview-title">See what you will learn.</h2><p>Two short explainers introduce how we choose useful work, build workflows, and keep human review.</p></div>
      <div className="ai30-training-recordings">{videos.map(video => <TrainingVideoPreview key={video.id} video={video} />)}</div>
    </section>
  );
}
